import { Badge } from "@hub/components/ui/badge";
import { Button } from "@hub/components/ui/button";
import { Input } from "@hub/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@hub/components/ui/table";
import { cn } from "@hub/lib/utils";
import { DriftBanner } from "@hub/ui/DriftBanner";
import { FleetStats } from "@hub/ui/FleetStats";
import { ReconcileButton } from "@hub/ui/ReconcileButton";
import { ArrowDown, ArrowUp, ChevronsUpDown, Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { apiFetch } from "./api";
import { relativeTime } from "./format";

interface InstanceRow {
  id: string;
  slug: string;
  host: string;
  ownerName: string | null;
  ownerEmail: string | null;
  desiredVersion: string | null;
  orgSlug: string;
  orgName: string;
  pinnedVersion: string | null;
  appVersion: string | null;
  runtimeVersion: string | null;
  runtimeOk: boolean | null;
  lastSeenAt: string | null;
  status: string;
  /** Non-null means this instance's Microsoft callback still needs to be
   *  added to the Entra app registration by hand -- see the detail page's
   *  own doc on this column. Surfaced here (not just on the detail page) so
   *  an operator provisioning several boxes in a session does not have to
   *  open each one to find out. */
  entraRedirectPendingAt: string | null;
  stale: boolean;
}

// Must match the `SORTABLE` allowlist in @hub/routes/instances — sorting
// happens in SQL there, this is just which headers become toggles.
type SortKey =
  | "slug"
  | "host"
  | "ownerName"
  | "orgName"
  | "desiredVersion"
  | "appVersion"
  | "runtimeVersion"
  | "runtimeOk"
  | "lastSeenAt"
  | "status";

// Running and Runtime get separate headers (rather than folding runtimeVersion
// into the Running column as a quiet sub-line with no toggle of its own) so
// every value the SORTABLE allowlist in @hub/routes/instances exposes is
// actually reachable from the page — "who is behind" (runtimeVersion) is one
// of the sorts an operator wants, same as appVersion/desiredVersion.
const COLUMNS: Array<{ key: SortKey; label: string }> = [
  { key: "ownerName", label: "Person" },
  { key: "orgName", label: "Org" },
  { key: "host", label: "Host" },
  { key: "status", label: "Status" },
  { key: "appVersion", label: "Running" },
  { key: "runtimeVersion", label: "Runtime" },
  { key: "desiredVersion", label: "Desired" },
  { key: "runtimeOk", label: "Health" },
  { key: "lastSeenAt", label: "Last seen" },
];

function paramsFromLocation() {
  const params = new URLSearchParams(window.location.search);
  const sort = params.get("sort") ?? "slug";
  return {
    sort: (COLUMNS.some((c) => c.key === sort) || sort === "slug" ? sort : "slug") as SortKey,
    dir: params.get("dir") === "desc" ? ("desc" as const) : ("asc" as const),
    q: params.get("q") ?? "",
  };
}

/**
 * The fleet table. Sorting and filtering are query params sent straight to
 * `GET /api/instances` — the server does the ordering/matching in SQL (see
 * @hub/routes/instances); this component never re-sorts or re-filters rows
 * it already has.
 */
export function InstanceTable({ orgSlug }: { orgSlug?: string } = {}) {
  const initial = paramsFromLocation();
  const [sort, setSort] = useState<SortKey>(initial.sort);
  const [dir, setDir] = useState<"asc" | "desc">(initial.dir);
  const [q, setQ] = useState(initial.q);
  const [rows, setRows] = useState<InstanceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Inside one org every row has the same org, so that column is noise.
  const columns = orgSlug ? COLUMNS.filter((c) => c.key !== "orgName") : COLUMNS;

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("sort", sort);
    params.set("dir", dir);
    if (q) params.set("q", q);
    const fetchParams = new URLSearchParams(params);
    if (orgSlug) fetchParams.set("org", orgSlug);
    window.history.replaceState(null, "", `${window.location.pathname}?${params}`);

    const controller = new AbortController();
    // Debounce so keystrokes in the filter box don't fire one request each.
    const timer = setTimeout(() => {
      apiFetch<{ data: InstanceRow[] }>(`/api/instances?${fetchParams}`, {
        signal: controller.signal,
      })
        .then((body) => {
          setRows(body.data);
          setError(null);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setError(err instanceof Error ? err.message : "failed to load instances");
        });
    }, 200);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [sort, dir, q, orgSlug]);

  function toggleSort(key: SortKey) {
    if (key === sort) {
      setDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSort(key);
      setDir("asc");
    }
  }

  return (
    <div className="w-full">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        {orgSlug ? null : (
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Helm Hub</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Every Helm instance, the version it runs, and whether it is still answering.
            </p>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Filter by slug, host, or owner"
              aria-label="Filter instances"
              className="w-72 pl-9"
            />
          </div>
          {orgSlug ? null : (
            <>
              <ReconcileButton />
              <Button asChild>
                <a href="/instances/new">
                  <Plus /> Add instance
                </a>
              </Button>
            </>
          )}
        </div>
      </div>

      {orgSlug ? null : (
        <>
          <DriftBanner />
          <FleetStats />
        </>
      )}

      {error ? (
        <p className="mb-4 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.key}>
                  <button
                    type="button"
                    onClick={() => toggleSort(col.key)}
                    className="inline-flex items-center gap-1 hover:text-foreground"
                  >
                    {col.label}
                    {sort === col.key ? (
                      dir === "asc" ? (
                        <ArrowUp className="size-3" />
                      ) : (
                        <ArrowDown className="size-3" />
                      )
                    ) : (
                      <ChevronsUpDown className="size-3 opacity-50" />
                    )}
                  </button>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-muted-foreground"
                >
                  No instances match.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => <InstanceRowView key={row.id} row={row} showOrg={!orgSlug} />)
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function InstanceRowView({ row, showOrg }: { row: InstanceRow; showOrg: boolean }) {
  const pending = row.desiredVersion !== null && row.appVersion !== row.desiredVersion;
  // Quiet note while a live chat keeps the runtime container on the previous
  // image. The converger recreates it once the box is idle. Never an error.
  const runtimeBehind =
    row.appVersion !== null && row.runtimeVersion !== null && row.runtimeVersion !== row.appVersion;

  return (
    <TableRow>
      <TableCell>
        <a href={`/instances/${row.id}`} className="font-medium hover:underline">
          {row.ownerName ?? row.slug}
        </a>
        {row.ownerEmail ? (
          <div className="text-xs text-muted-foreground">{row.ownerEmail}</div>
        ) : null}
      </TableCell>
      {showOrg ? (
        <TableCell>
          <a href={`/orgs/${encodeURIComponent(row.orgSlug)}`} className="hover:underline">
            {row.orgName}
          </a>
        </TableCell>
      ) : null}
      <TableCell>
        <a
          href={`https://${row.host}`}
          target="_blank"
          rel="noreferrer"
          className="hover:underline"
        >
          {row.host}
        </a>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={row.status} />
          {/* Independent of row.status on purpose -- a box can reach
              `running` with this still set, and that is exactly when an
              operator most needs to see it, not less (see the detail
              page's own note on the same rule). */}
          {row.entraRedirectPendingAt ? (
            <Badge
              variant="warning"
              title="Microsoft callback needs to be added to the Entra app registration by hand"
            >
              Entra pending
            </Badge>
          ) : null}
        </div>
      </TableCell>
      <TableCell>{row.appVersion ?? "—"}</TableCell>
      <TableCell>
        <span>{row.runtimeVersion ?? "unknown"}</span>
        {runtimeBehind ? (
          <div className="text-xs text-muted-foreground">still on the previous image</div>
        ) : null}
      </TableCell>
      <TableCell>
        <span className="mr-2">{row.desiredVersion ?? "—"}</span>
        {row.pinnedVersion ? <Badge variant="outline">Pinned to {row.pinnedVersion}</Badge> : null}
        {pending ? <Badge variant="warning">pending</Badge> : null}
      </TableCell>
      <TableCell>
        <HealthDot row={row} />
      </TableCell>
      <TableCell className={cn(row.stale && "text-muted-foreground")}>
        {relativeTime(row.lastSeenAt)}
      </TableCell>
    </TableRow>
  );
}

/** The row's lifecycle state -- distinct from HealthDot's heartbeat-derived
 *  freshness. Without this, a box stuck at `error` reads identically to one
 *  that is merely `stale` on this page: same missing version, same grey
 *  health dot (see @hub/routes/instances' own doc on InstanceListRow). */
function StatusBadge({ status }: { status: string }) {
  if (status === "running") {
    return <span className="text-xs text-muted-foreground">running</span>;
  }
  const variant = status === "error" ? "destructive" : "warning";
  return <Badge variant={variant}>{status}</Badge>;
}

function HealthDot({ row }: { row: InstanceRow }) {
  const color = row.stale
    ? "bg-muted-foreground/40"
    : row.runtimeOk === true
      ? "bg-[var(--success)]"
      : row.runtimeOk === false
        ? "bg-destructive"
        : "bg-muted-foreground/40";
  const label = row.stale
    ? "stale"
    : row.runtimeOk === true
      ? "healthy"
      : row.runtimeOk === false
        ? "unhealthy"
        : "unknown";

  return (
    <span className="inline-flex items-center gap-2">
      <span className={cn("size-2.5 rounded-full", color)} aria-hidden="true" />
      <span className="text-xs text-muted-foreground">{label}</span>
    </span>
  );
}
